import { config } from '@/app/config'
import { currencyExponent } from '@/lib/format'
import type { MatchingFields } from '@/lib/meta-matching'

export type AnalyticsEvent =
  | 'page_view'
  | 'view_content'
  | 'add_to_cart'
  | 'sign_up'
  | 'generate'
  | 'generate_success'
  | 'begin_checkout'
  | 'add_payment_info'
  | 'purchase'
  | 'lead'
  | 'contact'

export type EventProps = Record<string, unknown>
type Destination = (event: AnalyticsEvent, props: EventProps, eventId: string) => void

const META_EVENTS: Partial<Record<AnalyticsEvent, string>> = {
  page_view: 'PageView',
  view_content: 'ViewContent',
  add_to_cart: 'AddToCart',
  sign_up: 'CompleteRegistration',
  begin_checkout: 'InitiateCheckout',
  add_payment_info: 'AddPaymentInfo',
  purchase: 'Purchase',
  lead: 'Lead',
  contact: 'Contact',
}
const META_SCRIPT = 'https://connect.facebook.net/en_US/fbevents.js'
const INTERACTIONS = ['pointerdown', 'keydown', 'touchstart', 'scroll'] as const
const IDLE_TIMEOUT = 3000

let identified: Promise<void> = Promise.resolve()

const dataLayer: Destination = (event, props, eventId) => {
  ;(window.dataLayer ??= []).push({ event, eventId, ...props })
}

function afterInteractionOrIdle(run: () => void) {
  let started = false
  const start = () => {
    if (started) return
    started = true
    for (const name of INTERACTIONS) window.removeEventListener(name, start)
    run()
  }
  const whenIdle = () => {
    if (typeof window.requestIdleCallback === 'function')
      window.requestIdleCallback(start, { timeout: IDLE_TIMEOUT })
    else window.setTimeout(start, IDLE_TIMEOUT)
  }
  for (const name of INTERACTIONS) window.addEventListener(name, start, { passive: true })
  if (document.readyState === 'complete') whenIdle()
  else window.addEventListener('load', whenIdle, { once: true })
}

function loadScript(src: string) {
  const script = document.createElement('script')
  script.async = true
  script.src = src
  document.head.appendChild(script)
}

function installMetaPixel(id: string): MetaPixel {
  if (window.fbq) return window.fbq
  const fbq = function (...args: unknown[]) {
    if (fbq.callMethod) fbq.callMethod(...args)
    else fbq.queue.push(args)
  } as MetaPixel
  fbq.push = fbq
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.queue = []
  fbq.disablePushState = true
  fbq.allowDuplicatePageViews = true
  window.fbq = fbq
  window._fbq = fbq
  fbq('init', id)
  afterInteractionOrIdle(() => loadScript(META_SCRIPT))
  return fbq
}

if (config.clarityId) afterInteractionOrIdle(() => void import('@/lib/clarity'))

function metaParameters({ value, currency, offering, service, context, order }: EventProps) {
  const parameters: EventProps = {}
  if (typeof value === 'number' && typeof currency === 'string') {
    parameters.value = value / 10 ** currencyExponent(currency)
    parameters.currency = currency
  }
  if (typeof offering === 'string') {
    parameters.content_ids = [offering]
    parameters.content_type = 'product'
    parameters.contents = [{ id: offering, quantity: 1 }]
    parameters.num_items = 1
  }
  if (typeof service === 'string') parameters.content_category = service
  if (typeof context === 'string') parameters.content_name = context
  if (typeof order === 'string') parameters.order_id = order
  return parameters
}

function metaPixel(id: string): Destination {
  return (event, props, eventId) => {
    const fbq = installMetaPixel(id)
    const standard = META_EVENTS[event]
    void identified.then(() => {
      if (standard) fbq('track', standard, metaParameters(props), { eventID: eventId })
      else fbq('trackCustom', event, props, { eventID: eventId })
    })
  }
}

const destinations: Destination[] = config.metaPixelId
  ? [dataLayer, metaPixel(config.metaPixelId)]
  : [dataLayer]

export function track(event: AnalyticsEvent, props: EventProps = {}) {
  const eventId =
    typeof props.order === 'string'
      ? props.order
      : typeof props.lead === 'string'
        ? props.lead
        : crypto.randomUUID()
  for (const send of destinations) send(event, props, eventId)
  if (import.meta.env.DEV) console.debug('[track]', { event, eventId, ...props })
}

export function matchUser(fields: Promise<MatchingFields>) {
  const id = config.metaPixelId
  if (!id) return
  identified = identified
    .then(async () => installMetaPixel(id)('init', id, await fields))
    .catch(() => undefined)
}

export function rememberMetaClick(search: string) {
  if (!config.metaPixelId || !search.includes('fbclid=')) return
  void import('@/lib/meta-matching').then(({ storeMetaClick }) => storeMetaClick(search))
}
